import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Container,
  Grid,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import GridViewIcon from "@mui/icons-material/GridView";
import ListIcon from "@mui/icons-material/List";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Search } from "@mui/icons-material";
import { connect } from "react-redux";
import axios from "axios";
import config from "../../../config";
import * as Actions from "../../../redux/actions";
import ListView from "../ListView";
import { motion } from "framer-motion";
import PetsIcon from "@mui/icons-material/Pets";

const Genetics = ({
  breedsRaw,
  loadBreeds,
  classes,
  loading,
  loadCategories,
  loadGenetics,
  loadGeneticsSubBreeds,
  subBreedGenetics,
  genetics,
  subBreedsRaw,
  loadSubBreeds,
}) => {
  const { categoryId, breedId, subBreedId, geneticId, varietyId } = useParams();
  const navigate = useNavigate();
  const catId = [318, 308, 288, 289, 310];

  const [view, setView] = useState("Grid");
  const [search, setSearch] = useState("");
  const [breedGenetics, setBreedGenetics] = useState([]);
  const [varietyGenetics, setVarietyGenetics] = useState([]);
  const [varietyName, setVarietyName] = useState("");
  const [redirectLoader, setRedirectLoader] = useState(false);
  const [isNavigate, setIsNavigate] = useState(false);

  useEffect(() => {
    if (catId.includes(parseInt(categoryId))) navigate("/classes");
    if (classes.length < 1) loadCategories();
    if (breedsRaw.length < 1) loadBreeds(categoryId);
    if (subBreedsRaw.length < 1) loadSubBreeds(breedId);
    if (varietyId) {
      const fetchVariety = async () => {
        try {
          const [gRes, vRes] = await Promise.all([
            axios.get(`${config.SERVER_URL}/api/v1/varieties/${varietyId}/genetics`, { headers: { "Content-Type": "application/json" } }),
            axios.get(`${config.SERVER_URL}/api/v1/varieties/${varietyId}`, { headers: { "Content-Type": "application/json" } }),
          ]);
          setVarietyGenetics(gRes.status === 200 ? gRes.data.data : []);
          setVarietyName(vRes.data?.data?.name || "");
        } catch (err) {
          setVarietyGenetics([]);
        }
      };
      fetchVariety();
    } else if (genetics.length < 1) {
      if (subBreedId) loadGeneticsSubBreeds(subBreedId);
      else loadGenetics(breedId);
    }
  }, []);

  const category = classes.find((cat) => cat.id === parseInt(categoryId));
  const breed = breedsRaw.find((b) => b.id === parseInt(breedId));
  const subBreed = subBreedsRaw.find((s) => s.id === parseInt(subBreedId));

  useEffect(() => {
    if (category && !category.active) navigate("/classes");
  }, [category]);

  useEffect(() => {
    setBreedGenetics(varietyId ? varietyGenetics : subBreedId ? subBreedGenetics : genetics);
  }, [loading, genetics, subBreedGenetics, varietyGenetics]);

  useEffect(() => {
    if (isNavigate && !loading) {
      if (subBreedGenetics.length > 0) {
        setRedirectLoader(false);
        navigate(`/classes/${categoryId}/breeds/${breedId}/genetics/${geneticId}/subbreeds`);
      } else {
        setRedirectLoader(false);
        navigate(`/listings/breed/${breedId}/genetics/${geneticId}`);
      }
    }
  }, [isNavigate, loading]);

  const filtered = breedGenetics.filter((item) =>
    !search || item.name.toLowerCase().includes(search.toLowerCase())
  );

  const pageTitle = varietyId ? varietyName : subBreedId ? subBreed?.name : breed?.name;

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#111" }}>
      {/* Hero Header */}
      <Box
        sx={{
          position: "relative",
          background: "linear-gradient(to bottom, #1a1a1a 0%, #111 100%)",
          borderBottom: "1px solid #222",
          py: { xs: 5, md: 7 },
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "radial-gradient(ellipse at 50% 0%, rgba(255,192,1,0.07) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
          {/* Breadcrumb */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3, flexWrap: "wrap" }}>
            {[
              { label: "Home", to: "/" },
              { label: "Classes", to: "/classes" },
              { label: category?.name, to: `/classes/${categoryId}/breeds` },
              {
                label: breed?.name,
                to: subBreedId
                  ? `/classes/${categoryId}/breeds/${breedId}/subbreeds`
                  : `/classes/${categoryId}/breeds/${breedId}/genetics`,
              },
              subBreedId && { label: subBreed?.name, active: true },
              !subBreedId && { label: "Genetics", active: true },
            ]
              .filter(Boolean)
              .map((crumb, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <Typography sx={{ color: "#555", fontSize: "13px" }}>/</Typography>}
                  {crumb.active ? (
                    <Typography sx={{ color: "#FFC001", fontSize: "13px", fontWeight: 600 }}>
                      {crumb.label}
                    </Typography>
                  ) : (
                    <Typography
                      component={Link}
                      to={crumb.to}
                      sx={{ color: "#777", fontSize: "13px", textDecoration: "none", "&:hover": { color: "#FFC001" } }}
                    >
                      {crumb.label}
                    </Typography>
                  )}
                </React.Fragment>
              ))}
          </Box>

          {/* Title */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "28px", sm: "36px", md: "48px" },
                color: "#fff",
                lineHeight: 1.1,
                mb: 1,
              }}
            >
              {pageTitle}{" "}
              <Box component="span" sx={{ color: "#FFC001" }}>
                Genetics
              </Box>
            </Typography>
            <Typography sx={{ color: "#777", fontSize: { xs: "13px", md: "15px" } }}>
              Select a genetic to browse listings
            </Typography>
          </Box>

          {/* Search + toggle */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
            <TextField
              placeholder="Search genetics..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: { xs: "100%", sm: "420px" },
                backgroundColor: "#1a1a1a",
                borderRadius: "10px",
                "& .MuiOutlinedInput-root": {
                  paddingRight: 0,
                  color: "#fff",
                  borderRadius: "10px",
                  "& fieldset": { borderColor: "#333" },
                  "&:hover fieldset": { borderColor: "#FFC001" },
                  "&.Mui-focused fieldset": { borderColor: "#FFC001" },
                },
                "& .MuiInputBase-input::placeholder": { color: "#666", opacity: 1 },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Box
                      sx={{
                        backgroundColor: "#FFC001",
                        height: "40px",
                        width: "44px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderTopRightRadius: "10px",
                        borderBottomRightRadius: "10px",
                      }}
                    >
                      <Search sx={{ color: "#000", fontSize: 20 }} />
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: "flex", backgroundColor: "#1a1a1a", border: "1px solid #333", borderRadius: "10px", overflow: "hidden" }}>
              <IconButton
                onClick={() => setView("Grid")}
                sx={{ borderRadius: 0, px: 1.5, backgroundColor: view === "Grid" ? "#FFC001" : "transparent", "&:hover": { backgroundColor: view === "Grid" ? "#e6ac00" : "#2a2a2a" } }}
              >
                <GridViewIcon sx={{ color: view === "Grid" ? "#000" : "#888", fontSize: 20 }} />
              </IconButton>
              <IconButton
                onClick={() => setView("List")}
                sx={{ borderRadius: 0, px: 1.5, backgroundColor: view === "List" ? "#FFC001" : "transparent", "&:hover": { backgroundColor: view === "List" ? "#e6ac00" : "#2a2a2a" } }}
              >
                <ListIcon sx={{ color: view === "List" ? "#000" : "#888", fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>

          {!loading && !redirectLoader && (
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography sx={{ color: "#555", fontSize: "13px" }}>
                {filtered.length} result{filtered.length !== 1 ? "s" : ""}{search ? ` for "${search}"` : ""}
              </Typography>
            </Box>
          )}
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
        {loading || redirectLoader ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress sx={{ color: "#FFC001" }} />
          </Box>
        ) : filtered.length > 0 ? (
          view === "Grid" ? (
            <>
              <Grid container spacing={2.5}>
                {filtered.map((item, index) => (
                  <Grid item xs={6} sm={4} md={3} lg={2.4} key={item.id}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.04 }}
                    >
                      <Box
                        component={Link}
                        to={`/listings/genetics/${item.id}`}
                        sx={{
                          display: "block",
                          textDecoration: "none",
                          cursor: "pointer",
                          borderRadius: "16px",
                          overflow: "hidden",
                          position: "relative",
                          height: { xs: "150px", sm: "180px", md: "200px" },
                          border: "1px solid #2a2a2a",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            borderColor: "#FFC001",
                            transform: "translateY(-5px)",
                            boxShadow: "0 12px 30px rgba(255,192,1,0.15)",
                            "& .card-bg": { filter: "blur(5px) brightness(0.3)", transform: "scale(1.1)" },
                            "& .card-label": { bottom: "14px" },
                            "& .card-badge": { opacity: 1, transform: "translateX(-50%) translateY(0)" },
                          },
                        }}
                      >
                        <Box className="card-bg" sx={{ position: "absolute", inset: 0, backgroundImage: item.image ? `url(${item.image})` : "none", backgroundColor: item.image ? "transparent" : "#1a1a1a", backgroundSize: "cover", backgroundPosition: "center", filter: "blur(0px) brightness(0.55)", transform: "scale(1.0)", transition: "all 0.4s ease" }} />
                        <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.2) 55%, transparent 100%)" }} />
                        <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", backgroundColor: "#FFC001", opacity: 0.9 }} />
                        {!item.image && (
                          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <PetsIcon sx={{ fontSize: 40, color: "#FFC001", opacity: 0.4 }} />
                          </Box>
                        )}
                        {item.brand_promotions?.length > 0 && (
                          <Box component="img" src={item.brand_promotions[0].image} alt={item.brand_promotions[0].name} sx={{ position: "absolute", top: 10, right: 10, width: 36, height: 36, borderRadius: "50%", border: "2px solid #FFC001", objectFit: "contain", backgroundColor: "#000" }} />
                        )}
                        <Box className="card-label" sx={{ position: "absolute", bottom: "10px", left: 0, right: 0, px: 1.5, transition: "bottom 0.3s ease" }}>
                          <Typography noWrap sx={{ color: "#fff", fontWeight: 700, fontSize: { xs: "13px", sm: "14px", md: "15px" }, textShadow: "0 1px 6px rgba(0,0,0,0.9)" }}>
                            {item.name}
                          </Typography>
                        </Box>
                        <Box className="card-badge" sx={{ position: "absolute", bottom: "36px", left: "50%", transform: "translateX(-50%) translateY(8px)", opacity: 0, transition: "all 0.3s ease", backgroundColor: "#FFC001", color: "#000", fontWeight: 700, fontSize: "11px", px: 1.5, py: 0.4, borderRadius: "20px", whiteSpace: "nowrap" }}>
                          View Listings →
                        </Box>
                      </Box>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ textAlign: "center", mt: 5 }}>
                <Box
                  component={Link}
                  to={subBreedId ? `/listings/subbreed/${subBreedId}` : `/listings/breed/${breedId}`}
                  sx={{ display: "inline-flex", alignItems: "center", gap: 1, backgroundColor: "#FFC001", color: "#000", fontWeight: 700, fontSize: { xs: "14px", md: "16px" }, px: 4, py: 1.5, borderRadius: "12px", textDecoration: "none", transition: "all 0.25s ease", "&:hover": { backgroundColor: "#e6ac00", transform: "translateY(-2px)" } }}
                >
                  <PetsIcon sx={{ fontSize: 20 }} />
                  See All {pageTitle} Listings
                </Box>
              </Box>
            </>
          ) : (
            <ListView categories={breedGenetics} page="genetics" search={search} />
          )
        ) : (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <PetsIcon sx={{ fontSize: 60, color: "#333", mb: 2 }} />
            <Typography sx={{ color: "#555", fontSize: "16px" }}>
              No genetics found{search ? ` for "${search}"` : ""}.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

const mapStateToProps = ({ Categories }) => ({
  breedsRaw: Categories.breeds,
  classes: Categories.classes,
  loading: Categories.loading,
  genetics: Categories.genetics,
  subBreedGenetics: Categories.subBreedGenetics,
  subBreedsRaw: Categories.subBreeds,
});

const mapDispatchToProps = (dispatch) => ({
  loadBreeds: (categoryId) => dispatch(Actions.thunkLoadBreeds(categoryId)),
  loadGenetics: (breedId) => dispatch(Actions.thunkLoadGenetics(breedId)),
  loadCategories: () => dispatch(Actions.thunkLoadCategories()),
  loadGeneticsSubBreeds: (geneticsId) => dispatch(Actions.thunkLoadSubBreedGenetics(geneticsId)),
  loadSubBreeds: (breedId) => dispatch(Actions.thunkLoadSubBreeds(breedId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Genetics);
